import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { ModalBody, Form, FormGroup, Label } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import ErrorBoundary from '@/components/ErrorBoundary'
import context from '@/context'
import { useLocalization } from '@/hooks/localization'
import { Dropzone } from '@/components/fileAttachments/dropzone'
import { CancelToken } from '@/utils/fetch'
import LoadingIndicator from '@/components/LoadingIndicator'
import { Input } from '@/components/inputs'
import { ConfirmFooter } from '@/dialogs/common/confirmFooter'
import { DataLoader } from '@/components/dataLoader'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { decodeVersionString } from '@/utils/versionConverter'

const actionTypes = {
  readFile: 'readFile',
  loading: 'loading',
  finishedLoading: 'finishedLoading',
  setError: 'setError'
}

const initialState = {
  id: '',
  code: '',
  name: '',
  latinName: '',
  version: null,
  data: null,
  loading: false,
  steps: 0,
  allowManualExecution: false
}
function reducer (state = { ...initialState }, action) {
  switch (action.type) {
    case actionTypes.loading:
      return { ...state, loading: true }
    case actionTypes.setError:
      return { ...initialState, error: action.data, loading: false }
    case actionTypes.readFile:
      return {
        ...state,
        loading: false,
        id: action.data.Id,
        code: action.data.Code,
        name: action.data.Name,
        latinName: action.data.LatinName,
        version: action.data.Version,
        allowManualExecution: action.data.AllowManualExecution === true ? action.data.AllowManualExecution : false,
        steps: action.data.Steps ? action.data.Steps.length : 0,
        error: '',
        data: action.data
      }
  }
}

const AddWorkflowVersionDialog = React.memo(function AddWorkflowVersionDialog ({ onAction, ...props }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { localize } = useLocalization()
  const cancellation = useRef(CancelToken.source())
  const versionDataSource = useCallback(
    () => context.api.workflowDefinitionService.getDefinitionVersion(state.id),
    [state.id]
  )

  const onActionButton = useCallback(() => {
    if (state.data) {
      onAction(state.data)
    } else {
      dispatch({ type: actionTypes.setError, data: localize('Common:InvalidFileType', ['.json']) })
    }
  }, [onAction, state.data, localize])

  const onDrop = useCallback(([file]) => {
    dispatch({ type: actionTypes.loading })
    const reader = new window.FileReader()
    reader.onabort = () => {
      dispatch({ type: actionTypes.setError, data: 'file reading was aborted' })
    }
    reader.onerror = () => {
      dispatch({ type: actionTypes.setError, data: 'file reading has failed' })
    }
    reader.onload = () => {
      if (file.type === 'application/json') {
        try {
          dispatch({ type: actionTypes.readFile, data: JSON.parse(reader.result) })
        } catch (err) {
          dispatch({ type: actionTypes.setError, data: `${err}` })
        }
      }
    }
    reader.readAsText(file)
  }, [])

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancellation.current.cancel('dialog closed')
    }
  }, [])
  const dropzoneAccept = useMemo(() => ['application/json'], [])
  return (
    <React.Fragment>
      <ValidationBoundary>
        <BasicHeader title='Common.AddWorkflow' {...props} />
        {state.loading
          ? <ModalBody><LoadingIndicator /></ModalBody>
          : <ModalBody>
            <ErrorBoundary>
              {!!state.id && <Form>
                <FormGroup>
                  <Label>{localize('Common.Id')}</Label>
                  <Validator name='id' value={state.id} required>
                    <Input value={state.id} readOnly />
                  </Validator>
                </FormGroup>
                <FormGroup>
                  <Label>{localize('Common.Name')}</Label>
                  <Validator name='name' value={state.name} required>
                    <Input value={state.name} readOnly />
                  </Validator>
                </FormGroup>
                <FormGroup>
                  <Label>{localize('Common.LatinName')}</Label>
                  <Validator name='latinName' value={state.latinName} required>
                    <Input value={state.latinName} readOnly />
                  </Validator>
                </FormGroup>
                <FormGroup>
                  <Label>{localize('Common.Steps')}</Label>
                  <Validator name='steps' value={state.steps} min={1}>
                    <Input value={state.steps} readOnly />
                  </Validator>
                </FormGroup>
                <DataLoader dataSource={versionDataSource} render={({ loading, data }) => loading
                  ? <LoadingIndicator /> : <FormGroup>
                    <Label>{localize('Common.Version')}</Label>
                    <Input value={decodeVersionString(data + 1)} readOnly />
                  </FormGroup>
                } />
                <Input name={'allowManualExecution'} value={state.allowManualExecution} readOnly hidden />
              </Form>}
              {!!state.error && <FormGroup>
                <div className='error-boundary'>
                  {state.error}
                </div>
              </FormGroup>}

              <Form style={{ width: '100%' }}>
                <FormGroup>
                  <Label>{localize('Common.OpenWorkflowFile')}</Label>
                  <Dropzone
                    uploadButtonText={'Common.DragAndDropFile'}
                    multiple={false}
                    accept={dropzoneAccept}
                    onDropAccepted={onDrop}
                  />
                </FormGroup>
              </Form>
            </ErrorBoundary>
          </ModalBody>
        }
        {!state.loading && <ValidationState render={({ isValid }) =>
          <ConfirmFooter onAction={onActionButton} disabled={!isValid} icon='save' {...props} />}
        />}
      </ValidationBoundary>
    </React.Fragment>
  )
})

export default {
  component: AddWorkflowVersionDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/addWorkflowVersion.js